import type { ResultCard } from "./result-card.js";
import { renderResultCard } from "./result-card-canvas.js";

/** What to show when the device has no share sheet for image files. */
export interface ShareFallback {
  /** Object URL of the PNG; revoke it when the dialog closes. */
  url: string;
  fileName: string;
  text: string;
}

export type ShareOutcome =
  | { kind: "shared" }
  | { kind: "cancelled" }
  | { kind: "fallback"; fallback: ShareFallback };

/**
 * Renders the card and hands it to the native share sheet. Devices that
 * cannot share files get the image back for the in-app fallback dialog.
 */
export async function shareResultCard(card: ResultCard): Promise<ShareOutcome> {
  const canvas = await renderResultCard(card);
  const blob = await toPng(canvas);
  const file = new File([blob], card.fileName, { type: "image/png" });
  if (canShareFiles(file)) {
    try {
      await navigator.share({
        files: [file],
        text: card.text,
        title: "Fairway Rivalry",
      });
      return { kind: "shared" };
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return { kind: "cancelled" };
      }
    }
  }
  return {
    kind: "fallback",
    fallback: {
      url: URL.createObjectURL(blob),
      fileName: card.fileName,
      text: card.text,
    },
  };
}

function canShareFiles(file: File): boolean {
  return (
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] })
  );
}

function toPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("The image could not be created."));
    }, "image/png");
  });
}
